/**
 * "Nhập danh sách khách" (STT 2.2, ADR-089) — chỉ chủ tiệm. Ba trạng thái, mỗi trạng thái MỘT nút chính:
 *   1. Dán      — ô dán lớn + "Chọn tệp CSV" → "Xem trước".
 *   2. Xem trước — đếm, dòng cần xem lên đầu (mỗi dòng một lý do) → "Nhập {n} khách".
 *   3. Kết quả  — "Đã nhập {n} khách" → "Xong".
 *
 * Máy chủ kiểm lại mọi thứ; ở đây chạy cùng hàm đọc bảng để xem trước tức thì. Danh sách (số điện thoại, tên)
 * chỉ nằm trong state của tờ này — không log, không console, không localStorage.
 */
import { ClipboardCopy, FileUp, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { type Api, ApiFailure, type KetQuaNhapKhach, ngayVn } from './api.ts'
import { docBangMaTep, TEP_TOI_DA_BYTE } from './doc-tep.ts'
import {
  type BangDoc,
  type BanXemTruoc,
  cauLoiNhap,
  cauThieuSo,
  cauVeCot,
  type DongHien,
  docBang,
  dungXemTruoc,
  khoaChoNoiDung,
  nutChinhKhiKhongMoi,
  nutNhap,
  nutXemTruoc,
  phanTheDem,
  TRANG_HIEN,
  tomTatKetQua,
  vanBanChuaNhap,
} from './nhap-khach-hien.ts'
import { KH } from './text/khach.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

type Buoc = 'dan' | 'xem' | 'xong'

const CAU_LOI_DOC: Record<'TRONG' | 'KHONG_THAY_COT_TEN' | 'QUA_DONG', string> = {
  TRONG: KH.trong,
  KHONG_THAY_COT_TEN: KH.thieuCotTen,
  QUA_DONG: KH.quaDong,
}

function DongNhap({ d }: { d: DongHien }) {
  return (
    <li className={`dong-nhap dong-nhap-${d.nhom}`}>
      <div className="dong-nhap-dau">
        <span className="dong-nhap-so">{KH.dongSo(d.soDong)}</span>
        <b className="dong-nhap-ten">{d.ten}</b>
      </div>
      {d.sdt !== '' && <span className="dong-nhap-sdt">{d.sdt}</span>}
      {d.lyDo.map((ly) => (
        <span key={ly} className={`dong-nhap-ly ${d.nhom === 'loi' ? 'loi' : ''}`}>
          {ly}
        </span>
      ))}
    </li>
  )
}

/**
 * Máy đã hiểu cột nào, và những điều chủ tiệm cần biết trước khi bấm "Nhập". `boDongCot`: câu về cột đã nằm trong
 * hộp cảnh báo (dán nhầm bảng) — không nói lại ở đây, để một màn không có hai hộp nói về cùng chuyện.
 */
function GhiChuDaHieu({
  bang,
  kq,
  khongUtf8,
  boDongCot,
}: {
  bang: BangDoc
  kq: KetQuaNhapKhach
  khongUtf8: boolean
  boDongCot: boolean
}) {
  const kieuMdy = kq.kieuNgay.ngaySinh === 'mdy' || kq.kieuNgay.lanCuoi === 'mdy'
  const coLuuY = bang.coTieuDe && bang.cot.includes('luuY')
  if (boDongCot && !coLuuY && !kieuMdy && !khongUtf8) return null
  return (
    <div className="nhap-ghi-chu">
      {!boDongCot && <p>{cauVeCot(bang)}</p>}
      {coLuuY && <p>{KH.ghiChuThanhLuuY}</p>}
      {kieuMdy && <p>{KH.ngayThangNam}</p>}
      {khongUtf8 && <p>{KH.khongUtf8}</p>}
    </div>
  )
}

/** Một nhóm trong danh sách xem trước: tiêu đề rõ + các dòng. Nhóm rỗng không hiện. */
function NhomDong({
  nhom,
  tieuDe,
  ds,
}: {
  nhom: 'loi' | 'chu-y' | 'khong-so'
  tieuDe: string
  ds: DongHien[]
}) {
  if (ds.length === 0) return null
  return (
    <section className={`nhom-nhap nhom-nhap-${nhom}`}>
      <h3 className="nhom-nhap-tieu-de">{tieuDe}</h3>
      <DanhSachDong ds={ds} />
    </section>
  )
}

/**
 * Chép chữ vào clipboard theo yêu cầu của chủ tiệm. Không có Clipboard API (trình duyệt cũ, không phải HTTPS) →
 * `false`, giao diện hiện vùng chữ để chép tay. Không ghi vào localStorage / log.
 */
async function cheoVaoClipboard(chu: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText !== undefined) {
      await navigator.clipboard.writeText(chu)
      return true
    }
  } catch {
    // rơi xuống cách dự phòng
  }
  try {
    const o = document.createElement('textarea')
    o.value = chu
    o.setAttribute('readonly', '')
    o.style.position = 'fixed'
    o.style.opacity = '0'
    document.body.appendChild(o)
    o.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(o)
    return ok
  } catch {
    return false
  }
}

/** Sau khi nhập: số hồ sơ đã có giữ nguyên, số khách quen được ghi là khách cũ, gợi ý Chăm khách. */
function KetQuaPhu({ kq, coLanCuoi }: { kq: KetQuaNhapKhach; coLanCuoi: boolean }) {
  return (
    <>
      {kq.soDaCoCapNhat > 0 && <p>{KH.daCoGhiKhachCu(kq.soDaCoCapNhat)}</p>}
      {coLanCuoi && kq.soMoi > 0 && <p className="chu-phu">{KH.chamKhachGoiY}</p>}
    </>
  )
}

/** Danh sách cắt `TRANG_HIEN` dòng mỗi lần — 2.000 dòng không bị vẽ một lượt. */
function DanhSachDong({ ds }: { ds: DongHien[] }) {
  const [soHien, setSoHien] = useState(TRANG_HIEN)
  return (
    <>
      <ul className="ds-nhap">
        {ds.slice(0, soHien).map((d) => (
          <DongNhap key={d.chiSo} d={d} />
        ))}
      </ul>
      {ds.length > soHien && (
        <button
          type="button"
          className="nut nut-vien nut-form"
          onClick={() => setSoHien((n) => n + TRANG_HIEN)}
        >
          {KH.xemThem}
        </button>
      )}
    </>
  )
}

export function NhapKhach({
  api,
  onDong,
  onXong,
}: {
  api: Api
  onDong: () => void
  /** Bấm "Xong" sau khi nhập: người gọi đóng tờ và làm mới danh sách khách. */
  onXong: () => void
}) {
  const [buoc, setBuoc] = useState<Buoc>('dan')
  const [chu, setChu] = useState('')
  const [khongUtf8, setKhongUtf8] = useState(false)
  const [loiDoc, setLoiDoc] = useState<string | null>(null)
  const [bang, setBang] = useState<BangDoc | null>(null)
  const [kq, setKq] = useState<KetQuaNhapKhach | null>(null)
  const [ban, setBan] = useState<BanXemTruoc | null>(null)
  const [ketQuaNhap, setKetQuaNhap] = useState<KetQuaNhapKhach | null>(null)
  const [dang, setDang] = useState(false)
  const [moKhac, setMoKhac] = useState(false)
  /** Dán nhầm bảng (không có số điện thoại nào): phải tick xác nhận mới được nhập. */
  const [xacNhanChiTen, setXacNhanChiTen] = useState(false)
  /** Bản xem lại các dòng của KẾT QUẢ nhập (dòng chưa nhập + lý do). */
  const [banKq, setBanKq] = useState<BanXemTruoc | null>(null)
  const [daChep, setDaChep] = useState(false)
  const [chepTay, setChepTay] = useState<string | null>(null)
  const [canhBaoDong, setCanhBaoDong] = useState(false)
  const khoa = useRef<{ noiDung: string; khoa: string } | null>(null)
  const dangRef = useRef(false)
  const oTep = useRef<HTMLInputElement>(null)

  async function chonTep(tep: File | undefined) {
    if (tep === undefined) return
    setLoiDoc(null)
    if (tep.size > TEP_TOI_DA_BYTE) {
      setLoiDoc(KH.tepLonQua)
      return
    }
    try {
      const r = docBangMaTep(await tep.arrayBuffer())
      if (!r.ok) {
        setLoiDoc(r.loi === 'QUA_LON' ? KH.tepLonQua : KH.tepKhongDoc)
        return
      }
      setChu(r.chu)
      setKhongUtf8(r.khongPhaiUtf8)
    } catch {
      setLoiDoc(KH.tepKhongDoc)
    }
  }

  async function xemTruoc() {
    if (dangRef.current) return
    setLoiDoc(null)
    const r = docBang(chu)
    if (r.loi !== null) {
      setLoiDoc(CAU_LOI_DOC[r.loi])
      return
    }
    dangRef.current = true
    setDang(true)
    try {
      const kqMay = await api.nhapKhach({ thu: true, dong: r.bang.dong })
      setBang(r.bang)
      setKq(kqMay)
      setBan(dungXemTruoc(r.bang, kqMay, ngayVn()))
      setMoKhac(false)
      setXacNhanChiTen(false)
      setBuoc('xem')
    } catch (e) {
      // Xem trước không ghi gì → câu của máy chủ / "Không có mạng" là đủ (không phải câu "chưa chắc đã nhập").
      setLoiDoc(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      dangRef.current = false
      setDang(false)
    }
  }

  async function nhap() {
    if (dangRef.current || bang === null) return
    dangRef.current = true
    setDang(true)
    setLoiDoc(null)
    // Khoá theo NỘI DUNG đã dán: bấm lại sau lỗi mạng = cùng khoá (máy chủ trả lại kết quả cũ, không nhập đôi).
    khoa.current = khoaChoNoiDung(khoa.current, chu, () => crypto.randomUUID())
    try {
      const r = await api.nhapKhach({ thu: false, dong: bang.dong }, khoa.current.khoa)
      setKetQuaNhap(r)
      setBanKq(dungXemTruoc(bang, r, ngayVn()))
      setDaChep(false)
      setChepTay(null)
      setCanhBaoDong(false)
      setBuoc('xong')
    } catch (e) {
      if (e instanceof ApiFailure) setLoiDoc(cauLoiNhap(e).cau)
      else setLoiDoc(T.serverFailed)
    } finally {
      dangRef.current = false
      setDang(false)
    }
  }

  const tieuDe = KH.nhapDs
  const nutXem = nutXemTruoc(chu, dang)
  const nutN = nutNhap(ban?.soMoi ?? 0, dang)
  const canTickChiTen = ban?.khongCoSoNao === true
  const chuaTick = canTickChiTen && !xacNhanChiTen

  async function chepChuaNhap() {
    if (bang === null || banKq === null) return
    const vb = vanBanChuaNhap(bang, banKq.chuaNhap)
    if (await cheoVaoClipboard(vb)) {
      setDaChep(true)
      setChepTay(null)
    } else setChepTay(vb)
  }

  function bamXong() {
    // Còn dòng chưa nhập mà chưa chép: nhắc MỘT lần rồi mới đóng (đóng là mất danh sách).
    if ((ketQuaNhap?.soLoi ?? 0) > 0 && !daChep && !canhBaoDong) {
      setCanhBaoDong(true)
      return
    }
    onXong()
  }

  return (
    <To nhan={tieuDe} onDong={onDong}>
      <header className="to-dau">
        <h2>{tieuDe}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon nut-icon-lon"
          aria-label={T.close}
          onClick={buoc === 'xong' ? bamXong : onDong}
        >
          <X size={22} />
        </button>
      </header>

      {buoc === 'dan' && (
        <>
          <div className="to-than nhap-khach-than">
            {loiDoc !== null && (
              <ThongBao loai="loi" lon onDong={() => setLoiDoc(null)}>
                {loiDoc}
              </ThongBao>
            )}
            <p className="nhap-huong-dan">{KH.nhapHuongDan}</p>
            <p className="chu-phu">{KH.nhapMeo}</p>
            <p className="chu-phu">{KH.nhapLuuY}</p>
            <div className="truong">
              <label className="nhan" htmlFor="nhap-khach-chu">
                {KH.oDanNhan}
              </label>
              <textarea
                id="nhap-khach-chu"
                className="o-nhap o-dan"
                rows={8}
                wrap="off"
                spellCheck={false}
                autoComplete="off"
                value={chu}
                onChange={(e) => {
                  setChu(e.target.value)
                  setKhongUtf8(false)
                }}
              />
            </div>
            <div>
              <input
                ref={oTep}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                hidden
                onChange={(e) => {
                  void chonTep(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <button
                type="button"
                className="nut nut-vien nut-form"
                onClick={() => oTep.current?.click()}
              >
                <FileUp size={18} />
                {KH.chonTep}
              </button>
            </div>
          </div>
          <footer className="to-chan">
            <button
              type="button"
              className="nut nut-chinh nut-lon"
              disabled={nutXem.tat}
              onClick={() => void xemTruoc()}
            >
              {dang ? KH.dangKiem : KH.xemTruoc}
            </button>
          </footer>
        </>
      )}

      {buoc === 'xem' && ban !== null && bang !== null && kq !== null && (
        <>
          <div className="to-than nhap-khach-than">
            {loiDoc !== null && (
              <ThongBao loai="loi" lon onDong={() => setLoiDoc(null)}>
                {loiDoc}
              </ThongBao>
            )}
            <GhiChuDaHieu bang={bang} kq={kq} khongUtf8={khongUtf8} boDongCot={canTickChiTen} />
            <div className="the-dem-nhap" role="status">
              {phanTheDem(ban).map((p) => (
                <span
                  key={p.chu}
                  className={`dem-phan ${p.kieu === 'khong-nhap' ? 'dem-khong' : p.kieu === 'dam' ? 'dem-dam' : ''}`}
                >
                  {p.chu}
                </span>
              ))}
            </div>
            {canTickChiTen && (
              // MỘT hộp: câu cảnh báo + chuyện cột (không để hai hộp nói ngược nhau).
              <ThongBao loai="canh-bao" lon>
                <b>{cauThieuSo(bang)}</b>
                <p className="nhap-canh-bao-phu">{cauVeCot(bang)}</p>
              </ThongBao>
            )}
            {canTickChiTen && (
              <label className="nhap-xac-nhan">
                <input
                  type="checkbox"
                  checked={xacNhanChiTen}
                  onChange={(e) => setXacNhanChiTen(e.target.checked)}
                />
                <span>{KH.chiCoTenXacNhan}</span>
              </label>
            )}
            <NhomDong nhom="loi" tieuDe={KH.nhomKhongNhap(ban.soKhongNhap)} ds={ban.chuaNhap} />
            <NhomDong nhom="chu-y" tieuDe={KH.nhomDaSua(ban.soDaSua)} ds={ban.daSua} />
            <NhomDong
              nhom="khong-so"
              tieuDe={KH.nhomKhongSo(ban.khongSo.length)}
              ds={ban.khongSo}
            />
            {ban.khac.length > 0 && (
              <>
                <button
                  type="button"
                  className="nut nut-nhat nut-form"
                  aria-expanded={moKhac}
                  onClick={() => setMoKhac((v) => !v)}
                >
                  {moKhac ? KH.anKhongCanSua : KH.xemKhongCanSua(ban.khac.length)}
                </button>
                {moKhac && <DanhSachDong ds={ban.khac} />}
              </>
            )}
          </div>
          <footer className="to-chan hai-nut">
            {nutN.hien ? (
              <>
                <button
                  type="button"
                  className="nut nut-vien nut-lon"
                  disabled={dang}
                  onClick={() => setBuoc('dan')}
                >
                  {KH.danLai}
                </button>
                <button
                  type="button"
                  className="nut nut-chinh nut-lon"
                  disabled={nutN.tat || chuaTick}
                  onClick={() => void nhap()}
                >
                  {nutN.nhan}
                </button>
              </>
            ) : nutChinhKhiKhongMoi(ban.soKhongNhap) === 'dan-lai' ? (
              // Không có khách mới nhưng còn dòng không nhập: sửa trong Excel rồi dán lại.
              <>
                <button type="button" className="nut nut-vien nut-lon" onClick={onDong}>
                  {KH.xong}
                </button>
                <button
                  type="button"
                  className="nut nut-chinh nut-lon"
                  onClick={() => setBuoc('dan')}
                >
                  {KH.danLai}
                </button>
              </>
            ) : (
              // Toàn khách đã có hồ sơ: không còn gì để làm — "Xong" là nút chính.
              <>
                <button
                  type="button"
                  className="nut nut-vien nut-lon"
                  onClick={() => setBuoc('dan')}
                >
                  {KH.danLai}
                </button>
                <button type="button" className="nut nut-chinh nut-lon" onClick={onDong}>
                  {KH.xong}
                </button>
              </>
            )}
          </footer>
        </>
      )}

      {buoc === 'xong' && ketQuaNhap !== null && (
        <>
          <div className="to-than nhap-khach-than">
            <ThongBao loai="thanh-cong" lon>
              <b>{KH.daNhap(ketQuaNhap.soMoi)}</b>
            </ThongBao>
            {ketQuaNhap.soLoi > 0 && (
              <ThongBao loai="canh-bao" lon>
                <b>{KH.chuaNhap(ketQuaNhap.soLoi)}</b>
              </ThongBao>
            )}
            <p>
              {(() => {
                const t = tomTatKetQua(ketQuaNhap)
                return KH.tongKetQua(t.tong, t.moi, t.daCo, t.chua)
              })()}
            </p>
            <KetQuaPhu kq={ketQuaNhap} coLanCuoi={bang?.cot.includes('lanCuoi') === true} />
            {banKq !== null && banKq.chuaNhap.length > 0 && (
              <>
                <p>{KH.chuaNhapHuongDan}</p>
                <DanhSachDong ds={banKq.chuaNhap} />
                {chepTay !== null && (
                  <>
                    <p>{KH.khongChep}</p>
                    <textarea
                      className="o-nhap nhap-chep-tay"
                      readOnly
                      wrap="off"
                      spellCheck={false}
                      value={chepTay}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </>
                )}
                {canhBaoDong && !daChep && (
                  <ThongBao loai="canh-bao" lon>
                    {KH.chuaChepCanhBao}
                  </ThongBao>
                )}
              </>
            )}
          </div>
          <footer
            className={`to-chan ${banKq !== null && banKq.chuaNhap.length > 0 ? 'chan-doc' : ''}`}
          >
            {banKq !== null && banKq.chuaNhap.length > 0 ? (
              <>
                <button
                  type="button"
                  className={`nut nut-lon ${daChep ? 'nut-vien' : 'nut-chinh'}`}
                  onClick={() => void chepChuaNhap()}
                >
                  <ClipboardCopy size={18} />
                  {daChep ? KH.daChepNut : KH.chepChuaNhap}
                </button>
                <button
                  type="button"
                  className={`nut nut-lon ${daChep ? 'nut-chinh' : 'nut-vien'}`}
                  onClick={bamXong}
                >
                  {canhBaoDong && !daChep ? KH.vanDong : KH.xong}
                </button>
              </>
            ) : (
              <button type="button" className="nut nut-chinh nut-lon" onClick={onXong}>
                {KH.xong}
              </button>
            )}
          </footer>
        </>
      )}
    </To>
  )
}
